/**
 * Гайд по MCP для вкладки «MCP» окна «Документация» и окна в embed.
 *
 * Только русский текст — решение заказчика (19.09.2026), поэтому контент живёт здесь,
 * а не в трёх локалях i18n; ярлыки вкладки/кнопки — в i18n. Адрес сервера подставляется
 * при рендере из origin ремоута (`{{MCP_URL}}`), поэтому текст верен на любом стенде.
 * Источник истины по инструментам — живой `GET /mcp/tools.json`, а не этот файл.
 */

import { API_URL } from '../../shared/api/apiUrl';

export const MCP_PATH = '/mcp';
export const MCP_TOOLS_JSON_PATH = '/mcp/tools.json';
export const MCP_DOCS_PATH = '/mcp/docs';
export const MCP_URL_PLACEHOLDER = '{{MCP_URL}}';
export const MCP_SERVER_NAME = 'idea-calc';
/**
 * Документ RFC 9728 сервера: он есть только там, где MCP-сервер ведёт вход через браузер
 * (`MCP_OAUTH_ENABLED=true`). Сборка фронта одна на все стенды, поэтому режим подключения
 * узнаётся у самого сервера, а не из переменной сборки.
 */
export const MCP_RESOURCE_METADATA_PATH = '/.well-known/oauth-protected-resource/mcp';

/** Как подключаться к серверу этого стенда. */
export type McpSignInMode = 'browser' | 'token';

export interface McpGuideSection {
  id: string;
  title: string;
  html: string;
  /** Раздел только для этого режима; без поля — для любого. */
  mode?: McpSignInMode;
}

export const MCP_GUIDE_TEXT = {
  title: 'Подключение AI-агента через MCP',
  server_label: 'Адрес MCP-сервера',
  command_label: 'Команда подключения для Claude Code',
  command_hint: 'Токен не нужен: при первом вызове инструмента клиент сам откроет вход через Aseller в браузере.',
  command_hint_token: 'Подставьте свой refresh-токен платформы вместо <refresh-токен> — вход через браузер на этом стенде не включён.',
  mode_checking: 'Проверяю, как подключаться к этому стенду…',
  copy_button: 'Скопировать команду',
  copied: 'Команда скопирована',
  copy_failed: 'Не удалось скопировать — выделите текст и скопируйте вручную',
  tools_title: 'Инструменты сервера',
  tools_loading: 'Загружаю список инструментов с сервера…',
  tools_error: 'Не удалось получить список инструментов с сервера',
  tools_count: (n: number) => `${n} инструментов`,
  docs_link: 'Полный гайд в браузере',
} as const;

/** Адрес сервера — origin ремоута: верен и в standalone, и в embed на любом стенде. */
export function mcpServerUrl(): string {
  return `${API_URL}${MCP_PATH}`;
}

/** Основной путь: без токена — личность клиент получит входом через Aseller в браузере. */
export function claudeCodeCommand(mcpUrl: string): string {
  return `claude mcp add --transport http ${MCP_SERVER_NAME} "${mcpUrl}"`;
}

/** Альтернатива для клиентов без входа через браузер: refresh-токен платформы в заголовке. */
export function claudeCodeTokenCommand(mcpUrl: string): string {
  return `${claudeCodeCommand(mcpUrl)} --header "Authorization: Bearer <refresh-токен>"`;
}

/**
 * Режим стенда по документу RFC 9728. Вход через браузер — только если документ есть и
 * называет ИМЕННО этот сервер: без переключателя там 404 от MCP-сервера или `index.html`
 * фронта, и совет «подключайтесь без токена» оставил бы пользователя без доступа.
 */
export async function detectSignInMode(mcpUrl: string, signal: AbortSignal): Promise<McpSignInMode> {
  const origin = new URL(mcpUrl).origin;
  const response = await fetch(`${origin}${MCP_RESOURCE_METADATA_PATH}`, { signal });
  if (!response.ok) return 'token';
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Не JSON — это страница фронта, а не документ сервера: входа через браузер нет.
    return 'token';
  }
  const resource = (body as { resource?: unknown } | null)?.resource;
  return typeof resource === 'string' && resource.replace(/\/+$/, '') === mcpUrl.replace(/\/+$/, '')
    ? 'browser'
    : 'token';
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const TOKEN_KINDS_HTML =
  '<ul>' +
  '<li><strong>Агент внутри платформы aseller</strong> — платформенный access-токен, он у платформы уже есть.</li>' +
  '<li><strong>Claude Code, Cursor, свой скрипт</strong> — платформенный refresh-токен: тот же, что вставляется ' +
  'в окно «Платформенный токен» при входе на этот стенд напрямую.</li>' +
  '</ul>' +
  '<p>Сервер сам разбирает, какой токен пришёл. Никаких ключей, паролей и своей базы пользователей у него нет: ' +
  'refresh-токен он обменивает на доступ через бэкенд калькулятора и держит только в памяти.</p>';

/** Разделы для режима подключения; `null` — режим ещё не известен: только общие. */
export function sectionsFor(mode: McpSignInMode | null): McpGuideSection[] {
  return MCP_GUIDE_SECTIONS.filter((section) => section.mode === undefined || section.mode === mode);
}

export const MCP_GUIDE_SECTIONS: readonly McpGuideSection[] = [
  {
    id: 'what',
    title: 'Что это',
    html:
      '<p><strong>MCP</strong> (Model Context Protocol) — стандартный способ дать AI-агенту доступ к инструментам. ' +
      'Этот сервер отдаёт агенту всё, что умеет калькулятор: данные Keepa, конкурентов, расчёт экономики, ' +
      '60-месячный прогноз, бюджет запуска, ваши сохранённые расчёты, ссылки «Поделиться», расчёты по карточке товара и экспорт файлов.</p>' +
      '<p>Агент работает <strong>от вашего имени</strong>: видит и меняет только ваши записи, а всё, что сохранил, ' +
      'помечается в истории тегом <strong>MCP</strong>.</p>',
  },
  {
    id: 'signin',
    title: 'Адрес и вход',
    mode: 'browser',
    html:
      `<p>Адрес сервера: <code>${MCP_URL_PLACEHOLDER}</code>. Добавьте его в Claude Code, Cursor или как коннектор ` +
      'claude.ai — <strong>без токена</strong>.</p>' +
      '<ol>' +
      '<li>При первом вызове инструмента клиент откроет браузер со страницей калькулятора: она покажет, какое приложение ' +
      'просит доступ и куда вернёт вход. Нажмите «Разрешить».</li>' +
      '<li>Войдите на странице платформы Aseller — своим логином и паролем платформы. У калькулятора своих паролей нет.</li>' +
      '<li>Браузер вернёт вас в приложение, и агент начнёт работать от вашего имени.</li>' +
      '</ol>' +
      '<p>Когда доступ истечёт или будет отозван, клиент сам снова откроет вход. Если страница сообщила, что подключение ' +
      'больше не действует, начните подключение заново в приложении и доведите вход до конца в том же браузере.</p>',
  },
  {
    id: 'token',
    title: 'Альтернатива: токен вручную',
    mode: 'browser',
    html:
      '<p>Для клиента без входа через браузер (свой скрипт, старая версия клиента) токен передаётся заголовком ' +
      '<code>Authorization: Bearer &lt;токен&gt;</code> в каждом запросе:</p>' +
      `<pre><code>${escapeHtml(claudeCodeTokenCommand(MCP_URL_PLACEHOLDER))}</code></pre>` +
      TOKEN_KINDS_HTML,
  },
  {
    id: 'token-main',
    title: 'Адрес и токен',
    mode: 'token',
    html:
      `<p>Адрес сервера: <code>${MCP_URL_PLACEHOLDER}</code>. В каждом запросе нужен заголовок ` +
      '<code>Authorization: Bearer &lt;токен&gt;</code>:</p>' +
      TOKEN_KINDS_HTML,
  },
  {
    id: 'platform',
    title: 'Подключение из агента платформы',
    html:
      `<p>Streamable-HTTP клиент на <code>${MCP_URL_PLACEHOLDER}</code> с заголовком ` +
      '<code>Authorization: Bearer &lt;access-токен пользователя&gt;</code>. Сервер stateless: сессии не нужны, ' +
      'каждый вызов инструмента — отдельный POST. Список инструментов и их JSON-схемы доступны и без MCP-клиента: ' +
      `<code>GET ${MCP_URL_PLACEHOLDER}/tools.json</code>.</p>`,
  },
  {
    id: 'scenarios',
    title: 'Сценарии',
    html:
      '<p><strong>Оценить ASIN.</strong> <code>keepa_lookup</code> → цена, FBA fee, вес → <code>calc_unit_economics</code> ' +
      'со своей закупкой → при желании <code>find_competitors</code> + <code>analyze_competitors</code>.</p>' +
      '<p><strong>Сохранить и поделиться.</strong> <code>results_save</code> → <code>share_create</code> → ссылка. ' +
      'Запись появится в вашей истории с тегом MCP.</p>' +
      '<p><strong>Прогноз на 5 лет.</strong> <code>calc_forecast_60m</code> — те же числа, что в таблице «Прогноз» здесь; ' +
      'план продаж и бюджет можно взять из сохранённой записи (<code>results_get</code>) и передать как есть.</p>',
  },
  {
    id: 'limits',
    title: 'Ограничения и ошибки',
    html:
      '<ul>' +
      '<li>Keepa — платный внешний API с квотой: агенту не стоит повторять один и тот же запрос; батч — не больше 20 ASIN.</li>' +
      '<li>Чужие записи для агента не существуют (404), как и для вас.</li>' +
      '<li>Файлы экспорта приходят строкой base64 внутри ответа.</li>' +
      '<li>Расчёты по карточке товара работают только с платформенным токеном.</li>' +
      '<li>Ошибка инструмента содержит HTTP-статус и текст бэкенда: <code>401</code> — токен, <code>502</code> — платформа или бэкенд недоступны, ' +
      '<code>404</code> — нет такой записи, <code>429</code> — лимит, подождите.</li>' +
      '</ul>',
  },
];
